import {
  AngleMode,
  CutoutPoint,
  CutoutTypeEnum,
  PanelBase,
  MasonsMitreOrientationEnum,
  OutlineSide,
} from 'cloud-shared-lib';
import * as _ from 'lodash';

/**
 * @param panel
 * @param direction
 */
export function rotatePanel(
  panel: PanelBase,
  direction: RotationDirectionEnum = RotationDirectionEnum.Clockwise
): void {
  rotatePanelSize(panel);
  rotatePanelCutouts(panel, direction);
  rotatePanelSides(panel, direction);
  rotateAngles(panel, direction);
  rotateButts(panel, direction);
}

/**
 * @param panel
 * @param direction
 */
function rotateAngles(panel: PanelBase, direction: RotationDirectionEnum) {
  if (direction === RotationDirectionEnum.Clockwise) {
    [
      panel.panelOutline.topLeft,
      panel.panelOutline.topRight,
      panel.panelOutline.bottomRight,
      panel.panelOutline.bottomLeft,
    ] = [
      panel.panelOutline.bottomLeft,
      panel.panelOutline.topLeft,
      panel.panelOutline.topRight,
      panel.panelOutline.bottomRight,
    ];
  } else {
    [
      panel.panelOutline.topLeft,
      panel.panelOutline.topRight,
      panel.panelOutline.bottomRight,
      panel.panelOutline.bottomLeft,
    ] = [
      panel.panelOutline.topRight,
      panel.panelOutline.bottomRight,
      panel.panelOutline.bottomLeft,
      panel.panelOutline.topLeft,
    ];
  }

  [
    panel.panelOutline.topLeft,
    panel.panelOutline.topRight,
    panel.panelOutline.bottomRight,
    panel.panelOutline.bottomLeft,
  ].forEach(a => {
    [a.cutInsideX, a.cutInsideY] = [a.cutInsideY, a.cutInsideX];
    [a.cutX, a.cutY] = [a.cutY, a.cutX];

    if (a.masonsMitreFemale && a.mode === AngleMode.MasonsMitreFemale) {
      a.masonsMitreFemale.orientation =
        a.masonsMitreFemale.orientation === MasonsMitreOrientationEnum.AlongHeight
          ? MasonsMitreOrientationEnum.AlongWidth
          : MasonsMitreOrientationEnum.AlongHeight;
    }
  });
}

/**
 * @param panel
 * @param direction
 */
function rotateButts(panel: PanelBase, direction: RotationDirectionEnum) {
  if (direction === RotationDirectionEnum.Clockwise) {
    [panel.l1, panel.l2, panel.w1, panel.w2] = [panel.w1, panel.w2, panel.l2, panel.l1];
  } else {
    [panel.l1, panel.l2, panel.w1, panel.w2] = [panel.w2, panel.w1, panel.l1, panel.l2];
  }
}

/**
 * @param panel
 * @param direction
 */
function rotatePanelSides(panel: PanelBase, direction: RotationDirectionEnum) {
  if (direction === RotationDirectionEnum.Clockwise) {
    [
      panel.panelOutline.topSide,
      panel.panelOutline.rightSide,
      panel.panelOutline.bottomSide,
      panel.panelOutline.leftSide,
    ] = [
      panel.panelOutline.leftSide,
      panel.panelOutline.topSide,
      panel.panelOutline.rightSide,
      panel.panelOutline.bottomSide,
    ];
  } else {
    [
      panel.panelOutline.topSide,
      panel.panelOutline.rightSide,
      panel.panelOutline.bottomSide,
      panel.panelOutline.leftSide,
    ] = [
      panel.panelOutline.rightSide,
      panel.panelOutline.bottomSide,
      panel.panelOutline.leftSide,
      panel.panelOutline.topSide,
    ];
  }

  [
    panel.panelOutline.topSide,
    panel.panelOutline.rightSide,
    panel.panelOutline.bottomSide,
    panel.panelOutline.leftSide,
  ].forEach(s => {
    if (s.fastenersSets?.length) {
      s.fastenersSets.forEach(f => {
        [f.x, f.y] = [f.y, f.x];
      });
    }

    if (s.customHoles?.length) {
      s.customHoles.forEach(h => {
        [h.x, h.y] = [h.y, h.x];
      });
    }
  });
}

/**
 * @param panel
 */
function rotatePanelSize(panel: PanelBase): void {
  [panel.height, panel.width] = [panel.width, panel.height];
}

/**
 * @param panel
 * @param direction
 */
function rotatePanelCutouts(panel: PanelBase, direction: RotationDirectionEnum) {
  panel.panelOutline.cutouts.forEach(c => {
    c.basePoint = getRotatedCutoutPoint(c.basePoint, direction);
    c.angle = getRotatedCutoutPoint(c.angle, direction);
    [c.x, c.y] = [c.y, c.x];

    if (c.type == CutoutTypeEnum.Rectangle) {
      [c.width, c.height] = [c.height, c.width];
    }
  });
}

/**
 * @param point
 * @param direction
 */
function getRotatedCutoutPoint(point: CutoutPoint, direction: RotationDirectionEnum): CutoutPoint {
  if (point === CutoutPoint.Center) {
    return point;
  }

  const cutoutPointRotateSequence = [
    CutoutPoint.BottomLeft,
    CutoutPoint.TopLeft,
    CutoutPoint.TopRight,
    CutoutPoint.BottomRight,
  ];

  const index = cutoutPointRotateSequence.indexOf(point);
  const offset = direction === RotationDirectionEnum.Clockwise ? 1 : -1;
  const rotatedIndex = crawlArray(cutoutPointRotateSequence, index, offset);

  return cutoutPointRotateSequence[rotatedIndex];
}

/**
 * @param array
 * @param index
 * @param n
 */
function crawlArray(array: any[], index: number, n: number): number {
  return (((index + n) % array.length) + array.length) % array.length;
}

export enum RotationDirectionEnum {
  Clockwise,
  Counterclockwise,
}
